import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchWithAuth } from '../../utils/authUtils';
import { SommaireTab } from './tabs/SommaireTab';
import { IdentiteTab } from './tabs/IdentiteTab';
import { AnnuaireSiteTab } from './tabs/AnnuaireSiteTab';
import { ConnexionEnvironnementsTab } from './tabs/ConnexionEnvironnementsTab';
import { ModulesTab } from './tabs/ModulesTab';
import { ArchitectureTab } from './tabs/ArchitectureTab';
import { FicheInstallationTab } from './tabs/FicheInstallationTab';
import { InterfacesTab } from './tabs/InterfacesTab';
import { WorkflowTab } from './tabs/WorkflowTab';
import { ChecklistTab } from './tabs/ChecklistTab';

const EngineeringFolder = () => {
  const { clientId } = useParams();
  const [client, setClient] = useState(null);
  const [engineeringData, setEngineeringData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('sommaire');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchClientData();
    fetchEngineeringData();
  }, [clientId]);

  const fetchClientData = async () => {
    try {
      const response = await fetchWithAuth(`/api/clients/${clientId}`);
      if (response.ok) {
        const data = await response.json();
        setClient(data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement du client:', error);
    }
  };

  const fetchEngineeringData = async () => {
    try {
      const response = await fetchWithAuth(`/api/clients/${clientId}/engineering`);
      if (response.ok) {
        const data = await response.json();
        setEngineeringData(data);
      } else if (response.status === 404) {
        // Initialiser avec des données vides si aucune donnée n'existe
        setEngineeringData(getEmptyEngineeringData());
      } else {
        // Pour toute autre erreur, initialiser avec des données vides
        console.error('Erreur serveur:', response.status, response.statusText);
        setEngineeringData(getEmptyEngineeringData());
      }
    } catch (error) {
      console.error('Erreur lors du chargement des données ingénierie:', error);
      // En cas d'erreur (comme JSON.parse qui échoue), initialiser avec des données vides
      setEngineeringData(getEmptyEngineeringData());
    } finally {
      setLoading(false);
    }
  };

  const getEmptyEngineeringData = () => ({
    sommaire: {
      identiteProjet: { responsable: 'Commerce', statut: 'A faire', responsableProjet: 'Projet', statutProjet: 'A faire', responsableRun: 'Indus / CSM', statutRun: 'N/A' },
      annuaireSite: { responsable: 'Commerce', statut: 'A faire', responsableProjet: 'Projet', statutProjet: 'A faire', responsableRun: 'Indus / CSM', statutRun: 'N/A' },
      connexionEnvironnements: { responsableProjet: 'Projet', statutProjet: 'A faire', responsableRun: 'Indus / CSM', statutRun: 'N/A' },
      checklist: { responsableProjet: 'Projet', statutProjet: 'A faire' },
      modules: { responsable: 'Commerce', statut: 'A faire', responsableProjet: 'Projet', statutProjet: 'A faire', responsableRun: 'Commerce', statutRun: 'N/A' },
      architectureProd: { responsable: 'Indus', statut: 'A faire', responsableRun: 'Indus / Projet', statutRun: 'N/A' },
      ficheInstallProd: { responsable: 'Projet', statut: 'A faire', responsableRun: 'Indus / Projet', statutRun: 'N/A' },
      matriceFluxProd: { responsable: 'Projet', statut: 'A faire', responsableRun: 'Indus / Projet', statutRun: 'N/A' },
      architectureQual: { responsable: 'Indus', statut: 'A faire', responsableRun: 'Indus / Projet', statutRun: 'N/A' },
      ficheInstallQual: { responsable: 'Projet', statut: 'A faire', responsableRun: 'Indus / Projet', statutRun: 'N/A' },
      matriceFluxQual: { responsable: 'Projet', statut: 'A faire', responsableRun: 'Indus / Projet', statutRun: 'N/A' },
      workflowSuiviConnecte: { responsable: 'Projet', statut: 'A faire', responsableRun: 'CSM / Projet', statutRun: 'N/A' },
      workflowDossierAdmin: { responsable: 'Projet', statut: 'A faire', responsableRun: 'CSM / Projet', statutRun: 'N/A' }
    },
    identite: {
      // Infos administratives
      entiteContractante: '',
      modeleEconomique: '',
      modeHebergement: '',
      signatureContratPrincipal: '',
      modulesAchetes: '',
      // Equipe Hoppen
      commercial: '',
      avantVente: '',
      csm: '',
      // Information client
      nomEtablissement: '',
      codeInterne: '',
      adresseAccueil: '',
      adresseLivraison: '',
      telephone: '',
      siret: '',
      tva: '',
      // SI Client
      gap: '',
      dpi: '',
      agenda: '',
      // Contexte ES
      nomSites: '',
      nombreLits: '',
      nombreSejoursAnnuels: '',
      // Particularités projet
      sla: '',
      devSpecifiques: '',
      autres: '',
      // Environnements client
      urlPortailPatientProd: '',
      urlPortailPatientQual: '',
      urlPortailSoignantProd: '',
      urlPortailSoignantQual: '',
      nomAppMobile: '',
      // Modalités accès
      processDeploiement: '',
      processMaintenance: '',
      processUrgence: '',
      // Jalons projet
      jalons: {
        signatureContrat: '',
        passationVenteChefProjet: '',
        nomModuleDeploye: '',
        chefProjetModule: '',
        kickoffClient: '',
        cadrageProjet: '',
        signatureMom: '',
        signatureVa: '',
        dateMiseProduction: '',
        signatureVsr: '',
        passationChefProjetSupport: '',
        renouvellementMaintenance: ''
      }
    },
    annuaireSite: {
      sites: []
    },
    connexionEnvironnements: {
      modulesVendus: '',
      options: '',
      typeHebergement: '',
      checklistBitwarden: false,
      checklistContacts: false,
      processConnexion: '',
      restrictionsReseau: ''
    },
    modules: {
      modules: []
    },
    architecture: {
      production: {
        type: 'onpremise',
        serveurs: [],
        infrastructure: {
          virtualisation: '',
          stockage: '',
          sauvegarde: '',
          monitoring: ''
        }
      },
      qualification: {
        type: 'onpremise',
        serveurs: [],
        infrastructure: {
          virtualisation: '',
          stockage: '',
          sauvegarde: '',
          monitoring: ''
        }
      }
    },
    ficheInstallation: {
      production: {
        versions: {},
        configuration: {},
        notes: ''
      },
      qualification: {
        versions: {},
        configuration: {},
        notes: ''
      }
    },
    interfaces: {
      production: [],
      qualification: []
    },
    workflow: {
      suiviConnecte: {
        actif: false,
        description: '',
        etapes: []
      },
      dossierAdministratif: {
        actif: false,
        description: '',
        etapes: []
      },
      autres: []
    },
    checklist: {
      miseEnPlaceSondes: { statut: '', commentaire: '' },
      partageProcessTicketing: { statut: '', commentaire: '' },
      informationsBitwarden: { statut: '', commentaire: '' },
      environnementQual: { statut: '', commentaire: '' },
      autres: []
    }
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetchWithAuth(`/api/clients/${clientId}/engineering`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(engineeringData)
      });

      if (response.ok) {
        alert('Données sauvegardées avec succès');
      } else {
        throw new Error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'sommaire', label: 'SOMMAIRE', icon: '📋' },
    { id: 'identite', label: 'Identité', icon: '🏢' },
    { id: 'annuaire', label: 'Annuaire Site', icon: '📍' },
    { id: 'connexion', label: 'Connexion aux environnements', icon: '🔐' },
    { id: 'modules', label: 'Modules', icon: '📦' },
    { id: 'architecture', label: 'Architecture', icon: '🏗️' },
    { id: 'installation', label: 'Fiche Installation', icon: '💿' },
    { id: 'interfaces', label: 'Interfaces', icon: '🔄' },
    { id: 'workflow', label: 'Workflow', icon: '🔀' },
    { id: 'checklist', label: 'Checklist', icon: '✅' }
  ];

  return (
    <div className="container mx-auto px-4 py-6">
      {/* En-tête */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <Link to={`/client/${clientId}`} className="text-blue-600 hover:text-blue-800 mb-2 inline-block">
            ← Retour au client
          </Link>
          <h1 className="text-3xl font-bold">{client?.name} - Dossier d'Ingénierie</h1>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className={`px-6 py-2 rounded-lg font-medium transition-colors ${
            saving 
              ? 'bg-gray-400 cursor-not-allowed' 
              : 'bg-green-600 hover:bg-green-700 text-white'
          }`}
        >
          {saving ? 'Sauvegarde...' : 'Sauvegarder'}
        </button>
      </div>

      {/* Onglets */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex flex-wrap gap-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-3 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Contenu des onglets */}
      <div className="bg-white rounded-lg shadow-md p-6">
        {engineeringData ? (
          <>
            {activeTab === 'sommaire' && <SommaireTab data={engineeringData.sommaire || {}} setData={setEngineeringData} />}
            {activeTab === 'identite' && <IdentiteTab data={engineeringData.identite || {}} setData={setEngineeringData} />}
            {activeTab === 'annuaire' && <AnnuaireSiteTab data={engineeringData.annuaireSite || {}} setData={setEngineeringData} />}
            {activeTab === 'connexion' && <ConnexionEnvironnementsTab data={engineeringData.connexionEnvironnements || {}} setData={setEngineeringData} />}
            {activeTab === 'modules' && <ModulesTab data={engineeringData.modules || {}} setData={setEngineeringData} />}
            {activeTab === 'architecture' && <ArchitectureTab data={engineeringData.architecture || {}} setData={setEngineeringData} />}
            {activeTab === 'installation' && <FicheInstallationTab data={engineeringData.ficheInstallation || {}} setData={setEngineeringData} />}
            {activeTab === 'interfaces' && <InterfacesTab data={engineeringData.interfaces || {}} setData={setEngineeringData} />}
            {activeTab === 'workflow' && <WorkflowTab data={engineeringData.workflow || {}} setData={setEngineeringData} />}
            {activeTab === 'checklist' && <ChecklistTab data={engineeringData.checklist || {}} setData={setEngineeringData} />}
          </>
        ) : (
          <div className="text-center py-10 text-gray-500">
            Erreur lors du chargement des données d'ingénierie. Les données vont être initialisées...
          </div>
        )}
      </div>
    </div>
  );
};

export default EngineeringFolder;